import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// ─── Unified nav — all features, permission-gated ────────────────────────────
// `permission` = action code required; omit = always visible.
// Users with NO custom role (empty permissions[]) see everything (super admin).
// Users WITH a custom role see only items matching their permissions.
const allNavItems = [
  // ── Dashboard ──────────────────────────────────────────────────────────────
  { to: '/admin/dash/home',    label: 'Dashboard',  icon: '📈', permission: 'DASH_VIEW' },
  { to: '/admin/dash/analyze', label: 'Analyze',    icon: '🔬', permission: 'DASH_ANALYZE' },
  { type: 'divider' },

  // ── Personal (KAM-style) ───────────────────────────────────────────────────
  { to: '/kam/home',    label: 'My Home',       icon: '🏠', permission: 'TICKET_MY_VIEW' },
  { to: '/kam/raise',   label: 'Raise Ticket',  icon: '➕', permission: 'TICKET_RAISE' },
  { to: '/kam/tickets', label: 'My Tickets',    icon: '🎫', permission: 'TICKET_MY_VIEW' },

  // ── Support ────────────────────────────────────────────────────────────────
  { to: '/support/dashboard', label: 'Support Dashboard', icon: '📊', permission: 'SUPPORT_VIEW' },
  { to: '/support/tickets',   label: 'All Support Tickets', icon: '🎧', permission: 'SUPPORT_VIEW' },
  { type: 'divider' },

  // ── Ticket Admin ───────────────────────────────────────────────────────────
  {
    type: 'group', id: 'tickets', label: 'Ticket Details', icon: '🎫',
    children: [
      { to: '/admin/enterprise', label: 'By Enterprise', icon: '🏢', permission: 'TICKET_VIEW' },
      { to: '/admin/kam',        label: 'By KAM',        icon: '👤', permission: 'TICKET_VIEW' },
      { to: '/admin/agent',      label: 'By Agent',      icon: '🎧', permission: 'TICKET_VIEW' },
      { to: '/admin/tickets',    label: 'All Tickets',   icon: '📋', permission: 'TICKET_VIEW' },
    ],
  },
  { type: 'divider' },

  // ── ECMS ───────────────────────────────────────────────────────────────────
  {
    type: 'group', id: 'ecms', label: 'ECMS', icon: '📦',
    children: [
      { to: '/admin/ecms/enterprises', label: 'Enterprises', icon: '🏗️', permission: 'ECMS_ENTERPRISES_VIEW' },
      { to: '/admin/ecms/invoices',    label: 'Billing',     icon: '💰', permission: 'ECMS_BILLING_VIEW' },
    ],
  },

  // ── iFleet ─────────────────────────────────────────────────────────────────
  {
    type: 'group', id: 'ifleet', label: 'iFleet', icon: '🚛',
    children: [
      { to: '/admin/ifleet/enterprises', label: 'Enterprises', icon: '🚛', permission: 'IFLEET_ENTERPRISES_VIEW' },
      { to: '/admin/ifleet/invoices',    label: 'Billing',     icon: '💰', permission: 'IFLEET_BILLING_VIEW' },
    ],
  },
  { type: 'divider' },

  // ── Admin ──────────────────────────────────────────────────────────────────
  { to: '/admin/specializations', label: 'Specializations', icon: '⚙️', permission: 'SPECIALIZATIONS_VIEW' },
  { to: '/admin/zeerow/config',   label: 'Zeerow Billing',  icon: '⚡', permission: 'ZEEROW_VIEW' },
  { to: '/admin/roles',           label: 'Role Management', icon: '🔐', permission: 'ROLE_MANAGE' },
  { to: '/admin/users',           label: 'User Management', icon: '👥', permission: 'USER_MANAGE' },
];

// Returns true if the user can see this item.
// No custom role (empty permissions[]) = super admin = sees everything.
function canSee(permission, userPermissions) {
  if (!permission) return true;
  if (!userPermissions || userPermissions.length === 0) return true;
  return userPermissions.includes(permission);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getGroupsOpenForPath(links, pathname) {
  const open = {};
  links.forEach(link => {
    if (link.type === 'group' && link.children) {
      if (link.children.some(c => pathname === c.to || pathname.startsWith(c.to + '/'))) {
        open[link.id] = true;
      }
    }
  });
  return open;
}

// ─── Component ────────────────────────────────────────────────────────────────
const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [collapsed, setCollapsed]   = useState(false);

  const userPermissions = user?.permissions || [];

  const links = useMemo(() => {
    if (!user) return [];
    return allNavItems.reduce((acc, link) => {
      if (link.type === 'divider') { acc.push(link); return acc; }
      if (link.type === 'group') {
        const visibleChildren = (link.children || []).filter(c => canSee(c.permission, userPermissions));
        if (visibleChildren.length > 0) acc.push({ ...link, children: visibleChildren });
        return acc;
      }
      if (canSee(link.permission, userPermissions)) acc.push(link);
      return acc;
    }, []);
  }, [user, userPermissions]);

  const [openGroups, setOpenGroups] = useState(() =>
    getGroupsOpenForPath(links, location.pathname)
  );

  const toggleGroup = (id) =>
    setOpenGroups(prev => ({ ...prev, [id]: !prev[id] }));

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + '/');

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <aside className={`flex-shrink-0 h-screen bg-primary text-white flex flex-col shadow-xl z-50 transition-all duration-200 ${collapsed ? 'w-14' : 'w-56'}`}>

      {/* Logo */}
      <div className="flex items-center justify-between px-3 py-4 border-b border-white/10">
        {!collapsed && (
          <div>
            <div className="font-bold text-sm tracking-wide leading-tight">OmniCard B2B</div>
            <div className="text-xs text-blue-200 font-normal mt-0.5">Operations Portal</div>
          </div>
        )}
        <button
          onClick={() => setCollapsed(c => !c)}
          className="p-1 rounded hover:bg-white/10 transition-colors ml-auto"
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {collapsed
              ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
              : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/>
            }
          </svg>
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-2 px-1.5 space-y-0.5">
        {links.map((link, idx) => {

          // Divider
          if (link.type === 'divider') {
            return <div key={idx} className="h-px bg-white/10 my-1.5 mx-1" />;
          }

          // Expandable group
          if (link.type === 'group') {
            const isOpen    = !!openGroups[link.id];
            const anyActive = link.children?.some(c => isActive(c.to));
            return (
              <div key={link.id}>
                <button
                  onClick={() => !collapsed && toggleGroup(link.id)}
                  title={collapsed ? link.label : undefined}
                  className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-sm transition-colors ${
                    anyActive
                      ? 'bg-white/20 text-white font-medium'
                      : 'text-blue-100 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <span className="text-base flex-shrink-0 w-4 text-center">{link.icon}</span>
                  {!collapsed && (
                    <>
                      <span className="flex-1 text-left truncate">{link.label}</span>
                      <svg
                        className={`w-3 h-3 flex-shrink-0 text-white/50 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/>
                      </svg>
                    </>
                  )}
                </button>

                {/* Children — hidden when collapsed */}
                {!collapsed && isOpen && (
                  <div className="mt-0.5 ml-2 border-l border-white/10 pl-1.5 space-y-0.5">
                    {link.children.map(child => (
                      <Link
                        key={child.to}
                        to={child.to}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-colors ${
                          isActive(child.to)
                            ? 'bg-white/20 text-white font-medium'
                            : 'text-blue-200 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        <span className="flex-shrink-0">{child.icon}</span>
                        <span className="truncate">{child.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          // Flat link
          return (
            <Link
              key={link.to}
              to={link.to}
              title={collapsed ? link.label : undefined}
              className={`flex items-center gap-2.5 px-2 py-2 rounded-lg text-sm transition-colors ${
                isActive(link.to)
                  ? 'bg-white/20 text-white font-medium'
                  : 'text-blue-100 hover:bg-white/10 hover:text-white'
              }`}
            >
              <span className="text-base flex-shrink-0 w-4 text-center">{link.icon}</span>
              {!collapsed && <span className="truncate">{link.label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* User info + logout */}
      {user && (
        <div className="border-t border-white/10 px-3 py-3">
          {!collapsed && (
            <div className="mb-2">
              <p className="text-xs font-medium text-white truncate">{user.name}</p>
              <p className="text-xs text-blue-300">{user.team || user.role}</p>
            </div>
          )}
          <button
            onClick={handleLogout}
            title="Logout"
            className={`flex items-center gap-2 w-full px-2 py-1.5 rounded-lg text-xs bg-white/10 hover:bg-white/20 transition-colors ${collapsed ? 'justify-center' : ''}`}
          >
            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2h5a2 2 0 012 2v1"/>
            </svg>
            {!collapsed && <span>Logout</span>}
          </button>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
